/**
 * MeetingDefaultsModal — edit the platform-wide default Zoom meeting settings.
 * Full-admin only. Sub-admin can view (used to pre-fill their form) via the same
 * GET endpoint, but sees no Edit CTA in the parent component.
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Badge } from '../ui/badge';
import { Settings2, RotateCcw, Loader2, Info } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FACTORY = {
  waiting_room: true,
  host_video: false,
  participant_video: false,
  mute_upon_entry: true,
  join_before_host: false,
  auto_recording: 'none',
};

const Row = ({ label, description, value, onChange, testId }) => (
  <div className="flex items-start justify-between py-2 gap-3 border-b border-gray-100 last:border-0">
    <div className="flex-1">
      <Label className="text-sm font-medium">{label}</Label>
      {description && <p className="text-xs text-gray-500 mt-0.5">{description}</p>}
    </div>
    <Switch checked={value} onCheckedChange={onChange} data-testid={testId} />
  </div>
);

const MeetingDefaultsModal = ({ open, onOpenChange, onSaved }) => {
  const [settings, setSettings] = useState(FACTORY);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [meta, setMeta] = useState(null);

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        setLoading(true);
        const { data } = await axios.get(`${API}/admin/live-classes/settings/defaults`);
        setSettings(data.defaults);
        setMeta({ is_customised: data.is_customised, updated_by: data.updated_by, updated_at: data.updated_at });
      } catch {
        toast.error('Failed to load defaults');
      } finally { setLoading(false); }
    })();
  }, [open]);

  const save = async () => {
    try {
      setSaving(true);
      await axios.put(`${API}/admin/live-classes/settings/defaults`, settings);
      toast.success('Platform defaults updated');
      onSaved?.(settings);
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally { setSaving(false); }
  };

  const reset = async () => {
    if (!window.confirm('Reset to factory (safer exam-prep) defaults?')) return;
    try {
      setSaving(true);
      const { data } = await axios.post(`${API}/admin/live-classes/settings/defaults/reset`);
      setSettings(data.defaults);
      toast.success('Defaults reset to factory');
      onSaved?.(data.defaults);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reset');
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto" data-testid="meeting-defaults-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Settings2 className="w-5 h-5" />
            Platform Meeting Defaults
          </DialogTitle>
          <DialogDescription>
            These defaults pre-fill the schedule form for every new class. Individual classes can still override.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
          </div>
        ) : (
          <>
            {meta?.is_customised && (
              <div className="p-2.5 bg-amber-50 border border-amber-200 rounded text-xs text-amber-800 flex gap-2 items-start">
                <Info className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                <span>Currently customised. Last changed by <strong>{meta.updated_by}</strong>.</span>
              </div>
            )}

            <div className="py-2 divide-y divide-gray-100">
              <Row label="Waiting room"
                description="Attendees wait in a lobby until the host admits them."
                value={settings.waiting_room}
                onChange={(v) => setSettings(s => ({ ...s, waiting_room: v }))}
                testId="default-waiting-room" />
              <Row label="Mute participants on entry"
                description="Attendees join muted; they can unmute themselves."
                value={settings.mute_upon_entry}
                onChange={(v) => setSettings(s => ({ ...s, mute_upon_entry: v }))}
                testId="default-mute-entry" />
              <Row label="Turn OFF participant video on entry"
                description="Attendees join with camera off."
                value={!settings.participant_video}
                onChange={(v) => setSettings(s => ({ ...s, participant_video: !v }))}
                testId="default-participant-video" />
              <Row label="Turn OFF host video on entry"
                description="Host joins with camera off (can turn on manually)."
                value={!settings.host_video}
                onChange={(v) => setSettings(s => ({ ...s, host_video: !v }))}
                testId="default-host-video" />
              <Row label="Allow participants to join before host"
                description="If OFF, the meeting doesn&apos;t start until the host launches it."
                value={settings.join_before_host}
                onChange={(v) => setSettings(s => ({ ...s, join_before_host: v }))}
                testId="default-join-before-host" />
              <div className="flex items-start justify-between py-2 gap-3">
                <div className="flex-1">
                  <Label className="text-sm font-medium">Auto recording</Label>
                  <p className="text-xs text-gray-500 mt-0.5">Automatically record sessions.</p>
                </div>
                <Select value={settings.auto_recording}
                  onValueChange={(v) => setSettings(s => ({ ...s, auto_recording: v }))}>
                  <SelectTrigger className="w-32 h-9" data-testid="default-auto-recording">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Off</SelectItem>
                    <SelectItem value="local">Local</SelectItem>
                    <SelectItem value="cloud">Cloud</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="p-2.5 bg-blue-50 border border-blue-200 rounded text-xs text-blue-800 flex gap-2 items-start">
              <Info className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
              <span><strong>Tip:</strong> To prevent participants from turning on their cameras, the host must click <em>Security &rarr; Suspend Participant Activities</em> during the meeting &mdash; Zoom&apos;s scheduling API does not expose a permanent camera-lock setting. <Badge variant="outline" className="ml-1 text-[10px]">Runtime-only</Badge></span>
            </div>

            <div className="flex gap-3 pt-4 border-t">
              <Button variant="outline" onClick={reset} disabled={saving} data-testid="defaults-reset-btn">
                <RotateCcw className="w-4 h-4 mr-2" />
                Reset to factory
              </Button>
              <Button onClick={save} disabled={saving} className="flex-1" data-testid="defaults-save-btn">
                {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving&hellip;</> : 'Save Defaults'}
              </Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export { FACTORY as FACTORY_MEETING_DEFAULTS };
export default MeetingDefaultsModal;
